import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import config from "@/config";
import { useOctraceClient } from "@/contexts/ApiClientContext";
import { io } from "socket.io-client";

import { ConnectionStatus, type ConnectionState, type TypedSocket, type WebSocketContextType } from "@/types/websocket";

type NamespaceSubscription = {
  namespace: string;
  query?: Record<string, string>;
  socket: TypedSocket;
};

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

type WebSocketProviderProps = {
  children: ReactNode;
};

export const WebSocketProvider = ({ children }: WebSocketProviderProps) => {
  const { accessToken, waitForAuth, isAuthReady } = useOctraceClient();
  const [connectionState, setConnectionState] = useState<ConnectionState>({
    status: ConnectionStatus.DISCONNECTED,
    reconnectAttempts: 0,
  });

  const namespacesRef = useRef<Map<string, NamespaceSubscription>>(new Map());
  const pendingConnectionsRef = useRef<Map<string, Promise<TypedSocket>>>(new Map());

  const subscribeToNamespace = useCallback(
    async (namespace: string, query?: Record<string, string>): Promise<TypedSocket> => {
      const namespaceKey = `${namespace}:${JSON.stringify(query || {})}`;

      const existing = namespacesRef.current.get(namespaceKey);
      if (existing) return existing.socket;

      const pending = pendingConnectionsRef.current.get(namespaceKey);
      if (pending) {
        console.log(`[WebSocket] Connection already in progress for ${namespaceKey}, reusing promise`);
        return pending;
      }

      // Create connection promise and store it immediately
      const connectionPromise = (async (): Promise<TypedSocket> => {
        try {
          // Wait for authentication to be ready
          await waitForAuth();

          if (!accessToken) {
            throw new Error("Failed to get authentication token - user not authenticated");
          }

          // Retry logic with exponential backoff
          const maxRetries = 3;
          const baseDelay = 1000; // 1 second
          let lastError: Error | null = null;

          for (let attempt = 0; attempt < maxRetries; attempt++) {
            try {
              const fullUrl = `${config.BACKEND_URL}${namespace}`;
              const socket = io(fullUrl, {
                transports: ["websocket", "polling"],
                timeout: 10000,
                reconnection: true,
                reconnectionAttempts: 5,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 5000,
                query,
                auth: { token: accessToken },
              }) as TypedSocket;

              // Wait for connection or error
              await new Promise<void>((resolve, reject) => {
                const connectTimeout = setTimeout(() => {
                  reject(new Error("Connection timeout after 30 seconds"));
                }, 30000);

                socket.on("connect", () => {
                  clearTimeout(connectTimeout);
                  setConnectionState({
                    status: ConnectionStatus.CONNECTED,
                    lastConnected: new Date(),
                    reconnectAttempts: 0,
                  });
                  resolve();
                });

                socket.on("connect_error", (error) => {
                  clearTimeout(connectTimeout);
                  setConnectionState({
                    status: ConnectionStatus.ERROR,
                    reconnectAttempts: attempt + 1,
                    error: error.message,
                  });
                  reject(error);
                });
              });

              socket.on("disconnect", (reason) => {
                setConnectionState({
                  status: ConnectionStatus.DISCONNECTED,
                  reconnectAttempts: 0,
                  error: reason,
                });
              });

              namespacesRef.current.set(namespaceKey, { namespace, query, socket });

              return socket;
            } catch (error) {
              lastError = error as Error;
              console.error(`[WebSocket] Connection attempt ${attempt + 1} failed:`, error);

              // Don't retry on last attempt
              if (attempt < maxRetries - 1) {
                const delay = baseDelay * Math.pow(2, attempt); // Exponential backoff: 1s, 2s, 4s
                console.log(`[WebSocket] Retrying in ${delay}ms...`);
                await new Promise((resolve) => setTimeout(resolve, delay));
              }
            }
          }

          throw new Error(`Failed to connect after ${maxRetries} attempts: ${lastError?.message}`);
        } finally {
          // Always remove from pending map when done (success or failure)
          pendingConnectionsRef.current.delete(namespaceKey);
        }
      })();

      // Store the promise immediately before any async work
      pendingConnectionsRef.current.set(namespaceKey, connectionPromise);

      return connectionPromise;
    },
    [accessToken, waitForAuth],
  );

  const unsubscribeFromNamespace = useCallback((namespace: string, query?: Record<string, string>): void => {
    const namespaceKey = `${namespace}:${JSON.stringify(query || {})}`;
    const subscription = namespacesRef.current.get(namespaceKey);

    if (subscription) {
      subscription.socket.disconnect();
      namespacesRef.current.delete(namespaceKey);
    }

    // Note: Don't remove from pendingConnectionsRef here!
    // Pending connections are cleaned up automatically when the promise completes (in the finally block).
    // Removing them here would allow duplicate subscriptions during React StrictMode re-renders.
  }, []);

  // Reconnect all active sockets when token changes
  useEffect(() => {
    if (!accessToken || !isAuthReady) return;

    const activeNamespaces = Array.from(namespacesRef.current.entries());

    if (activeNamespaces.length === 0) return;

    console.log(`[WebSocket] Token refreshed, reconnecting ${activeNamespaces.length} namespace(s)...`);

    // Reconnect each active namespace with the new token
    activeNamespaces.forEach(([namespaceKey, subscription]) => {
      const { namespace, query, socket } = subscription;

      // Disconnect old socket
      socket.disconnect();

      // Create new socket with refreshed token
      const fullUrl = `${config.BACKEND_URL}${namespace}`;
      const newSocket = io(fullUrl, {
        transports: ["websocket", "polling"],
        timeout: 10000,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        query,
        auth: { token: accessToken },
      }) as TypedSocket;

      newSocket.on("connect", () => {
        console.log(`[WebSocket] Reconnected namespace: ${namespace}`);
        setConnectionState({
          status: ConnectionStatus.CONNECTED,
          lastConnected: new Date(),
          reconnectAttempts: 0,
        });
      });

      newSocket.on("disconnect", (reason) => {
        setConnectionState({
          status: ConnectionStatus.DISCONNECTED,
          reconnectAttempts: 0,
          error: reason,
        });
      });

      newSocket.on("connect_error", (error) => {
        console.error(`[WebSocket] Reconnection error for ${namespace}:`, error);
        setConnectionState({
          status: ConnectionStatus.ERROR,
          reconnectAttempts: 0,
          error: error.message,
        });
      });

      // Update the namespace map with the new socket
      namespacesRef.current.set(namespaceKey, { namespace, query, socket: newSocket });
    });
  }, [accessToken, isAuthReady]);

  useEffect(() => {
    const namespacesMap = namespacesRef.current;
    return () => {
      namespacesMap.forEach((subscription) => {
        subscription.socket.disconnect();
      });
      namespacesMap.clear();
    };
  }, []);

  const contextValue: WebSocketContextType = {
    connectionState,
    subscribeToNamespace,
    unsubscribeFromNamespace,
    isConnected:
      namespacesRef.current.size > 0 && Array.from(namespacesRef.current.values()).some((sub) => sub.socket.connected),
  };

  return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>;
};

export default WebSocketProvider;

// eslint-disable-next-line react-refresh/only-export-components
export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};
